import { useState, useEffect, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import NetWorthChart from "@/features/dashboard/NetWorthChart";
import { TIMEFRAMES, RANGE_LABEL } from "@/features/dashboard/NetWorthCard";
import { groupMeta } from "@vaulzo/core/domain/groups";

const signed = (v) => (v >= 0 ? "+" : "−") + formatCurrency(Math.abs(v));
const PERIOD_WORD = {
  "1y": "In the last year",
  "6m": "In the last 6 months",
  "3m": "In the last 3 months",
  "1m": "In the last month",
};

/**
 * The top of an Assets tab: the group's total, its change over the chosen
 * period and the chart. Hovering the chart scrubs the figures to that day.
 *
 * For Investments it also shows what the holdings cost ("put in"), the
 * overall gain, and the period's change split into money you added and
 * what the market did; the chart gets the money-put-in floor.
 */
export default function GroupHeader({
  group,
  total,
  series = [],
  dateRange,
  onDateRangeChange,
  isLoading = false,
  putInToday = null,
}) {
  const meta = groupMeta(group);
  const isInvestments = group === "investments";
  const [hover, setHover] = useState(null);
  useEffect(() => setHover(null), [dateRange, group]);

  // Today's point is pinned to the live total so hero, chart and rows agree.
  const points = useMemo(() => {
    const pts = series.map((p) => ({ date: p.date, value: p[group] || 0 }));
    if (pts.length) pts[pts.length - 1] = { ...pts[pts.length - 1], value: total };
    return pts;
  }, [series, group, total]);

  const base = useMemo(() => {
    if (!isInvestments) return null;
    const b = series.filter((p) => p.put_in != null).map((p) => ({ date: p.date, value: p.put_in, estimated: !!p.estimated }));
    if (b.length && putInToday != null && b[b.length - 1].date === points[points.length - 1]?.date) {
      b[b.length - 1] = { ...b[b.length - 1], value: putInToday };
    }
    return b.length ? b : null;
  }, [series, isInvestments, putInToday, points]);

  const n = points.length;
  const scrubbed = hover != null ? points[hover] : null;
  const shownValue = scrubbed ? scrubbed.value : total;
  const first = points[0]?.value ?? total;
  const change = shownValue - first;
  const changePct = first ? Math.abs((change / first) * 100).toFixed(1) : "0.0";
  const startDate = points[0] ? parseISO(points[0].date) : null;
  const changeLabel = scrubbed
    ? `since ${format(startDate, "d MMM yyyy")}`
    : RANGE_LABEL[dateRange] || (startDate ? `since ${format(startDate, "MMM yyyy")}` : "");

  // Investments: money added vs market, between the period start and the shown day.
  let split = null;
  let estNote = "";
  if (isInvestments && base) {
    const byDate = new Map(base.map((b) => [b.date, b]));
    const startIdx = points.findIndex((p) => byDate.has(p.date));
    const endIdx = scrubbed ? hover : n - 1;
    if (startIdx >= 0 && endIdx >= startIdx && byDate.has(points[endIdx].date)) {
      const added = byDate.get(points[endIdx].date).value - byDate.get(points[startIdx].date).value;
      const market = points[endIdx].value - points[startIdx].value - added;
      const head = scrubbed
        ? `To ${format(parseISO(points[endIdx].date), "d MMM")}`
        : PERIOD_WORD[dateRange] || `Since ${format(parseISO(points[startIdx].date), "d MMM")}`;
      split = `${head}: you added ${signed(added)} · markets ${signed(market)}`;
      const firstExact = base.find((b) => !b.estimated);
      if (byDate.get(points[startIdx].date).estimated && firstExact) {
        estNote = ` · put in before ${format(parseISO(firstExact.date), "d MMM")} is estimated`;
      }
    } else if (scrubbed) {
      split = `No cost data on ${format(parseISO(scrubbed.date), "d MMM")}`;
    }
  }
  const overallGain = putInToday != null ? total - putInToday : null;

  return (
    <div className="animate-vz-fade">
      {/* Fixed rows, so nothing moves on hover or period change */}
      <div className="mb-[14px] flex items-center justify-between gap-4">
        <div className="text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">{meta.short}</div>
        <div className="inline-flex flex-none gap-[2px] rounded-[10px] bg-vaulzo-row p-[3px]">
          {TIMEFRAMES.map((t) => {
            const active = dateRange === t.value;
            return (
              <button
                key={t.value}
                onClick={() => onDateRangeChange(t.value)}
                disabled={isLoading}
                className={`rounded-[7px] px-[11px] py-[5px] text-[12.5px] transition-all disabled:opacity-60 ${
                  active
                    ? "bg-vaulzo-surface font-semibold text-vaulzo-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                    : "bg-transparent font-medium text-vaulzo-faint hover:text-vaulzo-ink"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="num text-[40px] font-bold leading-[0.95] tracking-[-0.04em] text-vaulzo-ink">{formatCurrency(shownValue)}</div>
      {isInvestments && overallGain != null && (
        <div className="num mt-[10px] text-sm text-vaulzo-muted">
          <span className="font-semibold text-vaulzo-ink">{formatCurrency(putInToday)}</span> put in ·{" "}
          <span className="font-semibold" style={{ color: overallGain >= 0 ? "rgb(var(--vz-accent))" : "rgb(var(--vz-danger))" }}>
            {signed(overallGain)} ({overallGain >= 0 ? "+" : "−"}{Math.abs((overallGain / putInToday) * 100).toFixed(1)}%)
          </span>{" "}
          gain
        </div>
      )}
      {n > 1 && (
        <div className="num mt-[14px] text-sm font-semibold" style={{ color: change >= 0 ? "rgb(var(--vz-accent))" : "rgb(var(--vz-danger))" }}>
          {change >= 0 ? "↑" : "↓"} {formatCurrency(Math.abs(change))} · {changePct}% {changeLabel}
        </div>
      )}
      {isInvestments && <div className="num mt-[5px] min-h-[18px] text-[13px] text-vaulzo-muted">{split}</div>}
      <div className="mb-6 mt-[10px] min-h-4 text-[12px] text-vaulzo-subtle">
        {meta.note}
        {estNote}
      </div>

      <div className="relative mb-11">
        {n === 0 ? (
          <div className="flex h-[170px] items-center justify-center rounded-lg bg-vaulzo-row/60 text-sm text-vaulzo-subtle">
            No history yet for this group
          </div>
        ) : (
          <>
            <NetWorthChart id={`g-${group}`} points={points} base={base} hoverIndex={hover} onHover={setHover} height={170} />
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-vaulzo-accent" aria-hidden />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
